// src/pages/TontineDetails.jsx — full details of one tontine, join button, pay-this-cycle button
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { ArrowRight, CheckCircle2, Clock3, CreditCard, ShieldCheck, Users } from "lucide-react";

const TontineDetails = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [tontine, setTontine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState("");
  const [trustBlock, setTrustBlock] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const fetchedId = useRef(null);
  const trust = useQuery({
    queryKey: ["trust-score"],
    queryFn: () => api.get("/member/trust-score").then(({ data }) => data),
    enabled: Boolean(user),
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  });

  const fetchTontine = useCallback(async () => {
    if (fetchedId.current === id) return;
    fetchedId.current = id;
    try {
      const { data } = await api.get(`/tontines/${id}`);
      setTontine(data);
    } catch (err) {
      fetchedId.current = null;
      setActionError(err.response?.data?.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchTontine();
  }, [fetchTontine]);

  const isMember = tontine?.members?.some((m) => String(m.user?._id || m.user) === String(user?._id));

  const handleJoin = async () => {
    setActionLoading(true);
    setActionError("");
    setTrustBlock(null);
    try {
      await api.post(`/tontines/${id}/join`);
      fetchedId.current = null;
      await fetchTontine();
    } catch (err) {
      const response = err.response?.data;
      if (err.response?.status === 403 && response?.requiredTrustScore !== undefined) {
        setTrustBlock({
          score: response.trustScore,
          required: response.requiredTrustScore,
          pointsNeeded: response.pointsNeeded,
          kycStatus: response.kycStatus,
          factors: response.trustFactors,
        });
        await trust.refetch();
      }
      setActionError(response?.message || t("common.error"));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePay = async () => {
    setActionLoading(true);
    setActionError("");
    try {
      navigate(`/tontines/${id}/pay?cycle=${tontine.currentCycle || 1}`);
    } catch (err) {
      setActionError(err.response?.data?.message || t("common.error"));
      setActionLoading(false);
    }
  };

  if (loading) return <div className="page-shell"><div className="mx-auto h-96 max-w-3xl animate-pulse rounded-2xl bg-gold-100/60" /></div>;
  if (!tontine) return <div className="page-shell"><p className="rounded-2xl border border-red-100 bg-red-50 p-5 text-red-700">{actionError}</p></div>;

  const memberCount = tontine.members?.length || 0;
  const isFull = memberCount >= tontine.maxMembers;
  const isAcceptingMembers = tontine.status === "forming" && !isFull;
  const trustScore = trust.data?.score;
  const meetsTrustRequirement = trustScore === undefined || trustScore >= tontine.minTrustScoreRequired;
  const kycStatus = trust.data?.kycStatus || user?.kycStatus;
  const meetsKycRequirement = !tontine.fsiIncluded || kycStatus === "approved";

  return (
    <div className="page-shell max-w-3xl">
      <div className="card p-6 sm:p-8">
        <div className="mb-2 flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between">
          <h1 className="break-words font-serif text-3xl font-bold text-gold-700">{tontine.name}</h1>
          <span className="status-pill status-pill--neutral">
            {t(`tontine.categories.${tontine.category}`)}
          </span>
        </div>
        <p className="mb-7 mt-3 leading-7 text-gray-500">{tontine.description || "A trusted community savings circle."}</p>

        <div className="mb-6 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-gray-500">{t("tontine.amount")}</p>
            <p className="font-medium">
              {tontine.amount} {tontine.currency?.toUpperCase() || "EUR"}
            </p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-gray-500">{t("tontine.frequency")}</p>
            <p className="font-medium capitalize">{tontine.frequency}</p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-gray-500">{t("tontine.members")}</p>
            <p className="font-medium">
              {memberCount}/{tontine.maxMembers}
            </p>
            <p className="mt-1 text-xs text-gray-500">Starts at {tontine.minMembers} members</p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-gray-500">{t("tontine.status")}</p>
            <p><span className={`status-pill ${tontine.status === "active" ? "status-pill--active" : tontine.status === "forming" ? "status-pill--pending" : "status-pill--neutral"}`}>{tontine.status}</span></p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-gray-500">{t("tontine.cycle")}</p>
            <p className="font-medium">{tontine.currentCycle || 0}</p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-gray-500">{t("dashboard.nextPayout")}</p>
            <p className="font-medium">
              {tontine.nextRotationDate ? new Date(tontine.nextRotationDate).toLocaleDateString() : "—"}
            </p>
          </div>
        </div>

        {user && !isMember && isAcceptingMembers && (
          <section className="mb-6 rounded-2xl border border-gold-100 bg-gold-50/60 p-5" aria-live="polite">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 shrink-0 text-gold-700" size={20} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-forest">{t("trust.eligibilityTitle")}</p>
                {trust.isLoading ? (
                  <p className="mt-1 text-sm text-gray-600">{t("common.loading")}</p>
                ) : trust.isError ? (
                  <div className="mt-1 text-sm text-red-700">
                    <p>{t("trust.loadError")}</p>
                    <button className="mt-1 font-semibold underline" onClick={() => trust.refetch()}>{t("common.retry")}</button>
                  </div>
                ) : (
                  <>
                    <p className={`mt-1 text-sm ${trustScore >= tontine.minTrustScoreRequired ? "text-emerald-800" : "text-amber-800"}`}>
                      {t("trust.scoreRequirement", { score: trustScore, required: tontine.minTrustScoreRequired })}
                    </p>
                    {tontine.fsiIncluded && kycStatus !== "approved" && (
                      <p className="mt-1 text-sm text-amber-800">{t("trust.kycRequired")}</p>
                    )}
                    {trust.data.improvements?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-bold uppercase tracking-wide text-gray-600">{t("trust.improveTitle")}</p>
                        <ul className="mt-1 list-inside list-disc space-y-1 text-sm text-gray-600">
                          {trust.data.improvements.map(({ id }) => <li key={id}>{t(`trust.improvements.${id}`)}</li>)}
                        </ul>
                      </div>
                    )}
                    {trustScore < tontine.minTrustScoreRequired && (
                      <TrustJoinSteps
                        score={trustScore}
                        required={tontine.minTrustScoreRequired}
                        kycStatus={kycStatus}
                        factors={trust.data.factors}
                        t={t}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </section>
        )}

        {actionError && <div className="mb-4 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-800" role="alert">
          <p className="font-semibold">{actionError}</p>
          {trustBlock && (
            <TrustJoinSteps
              score={trustBlock.score}
              required={trustBlock.required}
              kycStatus={trustBlock.kycStatus}
              factors={trustBlock.factors}
              t={t}
            />
          )}
        </div>}
        {searchParams.get("payment") === "success" && <p className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-700">Payment completed. Your contribution will appear here after Stripe confirms it.</p>}
        {searchParams.get("payment") === "cancelled" && <p className="mb-4 rounded-xl border border-gold-100 bg-gold-50 p-3 text-sm text-gold-700">Payment was cancelled. You can try again when you are ready.</p>}

        {!user && isAcceptingMembers ? (
          <Link to="/login" className="btn-primary w-full">
            {t("common.loginToJoin")} <ArrowRight size={16} />
          </Link>
        ) : !isMember && isAcceptingMembers ? (
          <button
            onClick={handleJoin}
            disabled={actionLoading || !meetsTrustRequirement || !meetsKycRequirement}
            className="btn-primary w-full"
          >
            {actionLoading ? t("common.loading") : <><Users size={17} /> {t("tontine.join")} <ArrowRight size={16} /></>}
          </button>
        ) : !isMember ? (
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 text-sm text-gray-600">
            <p className="font-semibold text-gray-900">
              {isFull ? "This circle is full." : "This circle is no longer accepting members."}
            </p>
            <p className="mt-1">
              {isFull
                ? `It has reached its maximum of ${tontine.maxMembers} members.`
                : `It started when it reached its minimum of ${tontine.minMembers} members.`}
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-5">
            <div className="flex items-start justify-between gap-4">
              <div><p className="text-sm font-semibold text-emerald-900">Your contribution is due</p><p className="mt-1 text-xs leading-5 text-emerald-800">Pay securely with Stripe. Card details are handled by Stripe and are never stored by iTontine.</p></div>
              <CreditCard className="shrink-0 text-emerald-700" size={22} />
            </div>
            <button onClick={handlePay} disabled={actionLoading} className="btn-primary mt-4 w-full">
              {actionLoading ? t("common.loading") : <><CreditCard size={17} /> Pay {tontine.amount} {tontine.currency?.toUpperCase() || "EUR"} <ArrowRight size={16} /></>}
            </button>
          </div>
        )}

        {/* Member list */}
        <div className="mt-8">
          <h2 className="mb-3 flex items-center gap-2 font-serif text-lg text-gray-900"><Users size={18} className="text-gold-600" /> {t("tontine.members")}</h2>
          <ul className="divide-y divide-gold-50">
            {tontine.members?.map((m) => (
              <li key={m.user?._id || m.position} className="flex flex-col gap-2 py-2 text-sm sm:flex-row sm:justify-between sm:gap-3">
                <span>
                  #{m.position} — {m.user?.name || "Member"}
                </span>
                <span className={`status-pill ${m.hasReceivedPayout ? "status-pill--active" : "status-pill--pending"}`}>
                  {m.hasReceivedPayout ? <><CheckCircle2 size={13} /> Paid out</> : <><Clock3 size={13} /> Pending</>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const TrustJoinSteps = ({ score, required, kycStatus, factors, t }) => (
  <div className="mt-3 space-y-3 text-sm">
    <p className="font-semibold">{t("trust.pointsNeeded", { points: Math.max(0, required - score) })}</p>
    <ol className="list-inside list-decimal space-y-2 text-gray-700">
      {kycStatus !== "approved" && (
        <li>
          {t(`trust.joinSteps.kyc.${kycStatus === "pending" ? "pending" : kycStatus === "rejected" ? "rejected" : "default"}`)}
        </li>
      )}
      {score < 40 ? (
        <li>{t("trust.joinSteps.belowStarter", { score, remaining: Math.max(0, 40 - score) })}</li>
      ) : (
        <>
          <li>{t("trust.joinSteps.starterCircle")}</li>
          <li>{t("trust.joinSteps.contributions")}</li>
        </>
      )}
      {factors?.seniority < 25 && (
        <li>{t("trust.joinSteps.accountHistory", { current: factors.seniority, maximum: 25 })}</li>
      )}
      <li>{t("trust.joinSteps.support")}</li>
    </ol>
    <div className="flex flex-wrap gap-3 pt-1">
      <Link to="/circles" className="font-semibold text-gold-800 underline">{t("trust.browseCircles")}</Link>
      <a href="mailto:support@itontine.com" className="font-semibold text-gold-800 underline">{t("trust.contactSupport")}</a>
    </div>
  </div>
);

export default TontineDetails;
